'use client'

import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import ConfigCheck from '../../../../components/ConfigCheck'
import BiometricCheck from '../../../../components/BiometricCheck'
import ProctoringGuidelines from '../../../../components/ProctoringGuidelines'
import Assessment from './assessment'
import moment from 'moment'
import Timer from 'components/Timer'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import Bulb from '../../../../components/icons/Bulb'
import { Modal, Button } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import {
  resetResponses,
  fetchAssessmentQuestions,
} from 'modules/Assessments/actions'
import selectors from '../../selectors'
import styled from 'styled-components'

import {
  PageContainer,
  ContentWrapper,
  StartButton,
  InstructionCard,
  InstructionHeader,
  InstructionTitle,
  InstructionDuration,
  InstructionDeadline,
  ImportantNotice,
  NoticeText,
  InstructionSectionTitle,
  InstructionList,
  InstructionListItem,
  TakeAssessmentButton,
  ContactInfo,
  ModalOverlay,
  ModalContent,
} from '../../Style'

// Custom Fullscreen Modal Styled Components
const FullscreenModalOverlay = styled(ModalOverlay)`
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
`

const FullscreenModalContent = styled(ModalContent)`
  max-width: 500px;
  padding: 30px;
  text-align: center;
`

const FullscreenTitle = styled.h2`
  color: #4318ff;
  margin-bottom: 20px;
`

const FullscreenDescription = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
`

const PrimaryButton = styled(Button)`
  background-color: #4318ff;
  color: white;
  font-weight: bold;
  height: 44px;
  border-radius: 4px;
  border: none;
  padding: 0 24px;
  font-size: 16px;

  &:hover {
    background-color: #3512cc;
    color: white;
  }
`

const RoleBasedInstructions = ({
  assessment,
  questions,
  totalQuestions,
  error,
  responses,
  saveQuestionResponse,
  studentId,
  setAssessment,
  isPractice = false,
  onBack,
  enableFullscreen = false,
}) => {
  const location = useLocation()
  const dispatch = useDispatch()

  const [showConfig, setShowConfig] = useState(false)
  const [biometricRequested, setBiometricRequested] = useState(false)
  const [allChecksComplete, setAllChecksComplete] = useState(false)
  const verificationEnabled = assessment?.allowVerification !== false
  // Role-based duration is per-set (admin-configured, else derived from the
  // question mix), so it comes from the API rather than a fixed number. It is
  // unknown for a broadcast row until the set is bound at start.
  const durationLabel = assessment?.duration_minutes
    ? `Duration - ${assessment.duration_minutes} minutes`
    : 'Duration - Varies by role set'
  const [showAssessment, setShowAssessment] = useState(false)
  const [isTimerActive, setIsTimerActive] = useState(false)
  const [timerPhase, setTimerPhase] = useState('initial')
  const [countdown, setCountdown] = useState(3)
  const [questionsLoaded, setQuestionsLoaded] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showFullscreenModal, setShowFullscreenModal] = useState(false)
  const [fullscreenPermissionGranted, setFullscreenPermissionGranted] = useState(false)
  const MAX_TAB_SWITCH_WARNINGS = 3

  // Get questions from Redux store
  const questionsFromRedux = useSelector(selectors.getQuestions)
  const questionsLoading = useSelector(selectors.getLoading)
  const questionsError = useSelector(selectors.getError)

  useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 1500)
  }, [])

  // Check if questions are loaded
  useEffect(() => {
    console.log('🔍 Questions check effect triggered:', {
      questionsFromRedux,
      hasData: !!questionsFromRedux,
      hasSections: !!questionsFromRedux?.sections,
      sectionsLength: questionsFromRedux?.sections?.length
    })

    if (questionsFromRedux && questionsFromRedux.sections) {
      const hasQuestions = questionsFromRedux.sections.some(
        section => section.questions && section.questions.length > 0
      )

      console.log('📊 Questions analysis:', {
        hasQuestions,
        sections: questionsFromRedux.sections.map(s => ({
          name: s.section_name,
          questionCount: s.questions?.length || 0
        }))
      })

      if (hasQuestions) {
        console.log('✅ Setting questionsLoaded to true')
        setQuestionsLoaded(true)
      } else {
        console.log('❌ No questions found in sections')
      }
    } else {
      console.log('❌ Questions data structure invalid')
    }
  }, [questionsFromRedux])

  // Function to enter fullscreen
  const enterFullscreen = async () => {
    try {
      const elem = document.documentElement

      if (elem.requestFullscreen) {
        await elem.requestFullscreen()
      } else if (elem.mozRequestFullScreen) {
        await elem.mozRequestFullScreen()
      } else if (elem.webkitRequestFullscreen) {
        await elem.webkitRequestFullscreen()
      } else if (elem.msRequestFullscreen) {
        await elem.msRequestFullscreen()
      }
      return true
    } catch (error) {
      console.error('Error entering fullscreen:', error)
      return false
    }
  }

  // Custom Fullscreen Permission Modal Component
  const FullscreenPermissionModal = () => {
    const requestFullscreenAccess = async () => {
      try {
        await enterFullscreen()

        // Check if fullscreen was successful
        setTimeout(() => {
          if (
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement
          ) {
            setFullscreenPermissionGranted(true)
            setShowFullscreenModal(false)
            setShowAssessment(true)
          } else {
            alert(
              'You must enable fullscreen to proceed with the assessment. This is required for proctoring.'
            )
          }
        }, 500)
      } catch (error) {
        console.error('Fullscreen error:', error)
        alert(
          'Fullscreen permission is required. Please try again or check your browser settings.'
        )
      }
    }

    return (
      <FullscreenModalOverlay>
        <FullscreenModalContent onClick={e => e.stopPropagation()}>
          <FullscreenTitle>Enable Fullscreen Mode</FullscreenTitle>
          <FullscreenDescription>
            This assessment requires fullscreen mode for proctoring purposes.
            You must enable fullscreen to continue.
          </FullscreenDescription>

          <FullscreenDescription>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ marginLeft: '27px' }}>
                <strong>Important:</strong> During this assessment:
              </span>
              <ul style={{ margin: '10px 0' }}>
                <li>Exiting fullscreen is not allowed</li>
                <li>
                  Switching tabs is not allowed (max {MAX_TAB_SWITCH_WARNINGS}{' '}
                  warnings)
                </li>
                <li>Browser navigation is disabled</li>
                <li>Key combinations like Alt+Tab are blocked</li>
              </ul>
              Violations may result in automatic submission of your assessment.
            </div>
          </FullscreenDescription>

          <PrimaryButton onClick={requestFullscreenAccess}>
            Accept & Enter Fullscreen
          </PrimaryButton>
        </FullscreenModalContent>
      </FullscreenModalOverlay>
    )
  }

  const handleEnterFullscreen = async () => {
    try {
      if (isPractice) {
        try {
          const elem = document.documentElement
          if (elem.requestFullscreen) {
            await elem.requestFullscreen()
          } else if (elem.mozRequestFullScreen) {
            await elem.mozRequestFullScreen()
          } else if (elem.webkitRequestFullscreen) {
            await elem.webkitRequestFullscreen()
          } else if (elem.msRequestFullscreen) {
            await elem.msRequestFullscreen()
          }
        } catch (fullscreenError) {
          console.error('Fullscreen error in instruction.js:', fullscreenError)
        }

        setFullscreenPermissionGranted(true)
        setShowAssessment(true)
      } else {
        const elem = document.documentElement
        if (elem.requestFullscreen) {
          await elem.requestFullscreen()
        } else if (elem.mozRequestFullScreen) {
          await elem.mozRequestFullScreen()
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen()
        }
        setFullscreenPermissionGranted(true)
        setShowFullscreenModal(false)
      }
    } catch (error) {
      console.error('Error in handleEnterFullscreen:', error)
      if (isPractice) {
        setShowAssessment(true)
      }
    }
  }

  const handleTakeAssessment = () => {
    setShowConfig(true)
  }

  const handleCancelConfig = () => {
    setShowConfig(false)
  }

  const handleStart = async () => {
    if (!allChecksComplete) return

    if (assessment?.assessment_assigned_id) {
      dispatch(resetResponses())

      dispatch({
        type: 'SET_CURRENT_ASSESSMENT_ID',
        payload: {
          assessment_id: assessment.assessment_id || assessment.id,
          assessment_set_id: assessment.assessment_set_id,
          assessment_assigned_id: assessment.assessment_assigned_id,
          allowProctoring: assessment.allowProctoring || false,
          title: assessment.title,
          type: assessment.type,
          isPractice: assessment.isPractice || isPractice,
          isCorporate: assessment.isCorporate,
          roleName: assessment.roleName,
          seniority: assessment.seniority,
        },
      })

      const result = await dispatch(fetchAssessmentQuestions(assessment.assessment_assigned_id))
      if (!result || !result.success) {
        console.error('Failed to fetch assessment questions')
        return
      }
    } else {
      return
    }

    if (isPractice) {
      localStorage.setItem('isPracticeAssessment', 'true')
    }

    setIsTimerActive(true)
    setShowConfig(false)
    setTimerPhase('initial')

    setTimeout(() => {
      setTimerPhase('countdown')
      setCountdown(3)

      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            setTimerPhase('start')

            setTimeout(() => {
              if (isPractice || enableFullscreen) {
                setShowFullscreenModal(true)
              } else {
                setShowAssessment(true)
              }
            }, 1000)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 2000)
  }

  const handleBackAction = () => {
    if (isPractice && onBack) {
      onBack()
    } else {
      setAssessment(null)
    }
  }

  // Early return for loading state
  if (loading) {
    return <LoadingSpinner />
  }

  if (showAssessment && questionsLoaded) {
    console.log('🚀 Showing Assessment Component with data:')
    console.log('   - questionsFromRedux:', questionsFromRedux)
    console.log('   - questionsLoaded:', questionsLoaded)
    console.log('   - totalQuestions:', totalQuestions)

    return (
      <Assessment
        questions={{ data: questionsFromRedux }}
        totalQuestions={totalQuestions}
        loading={questionsLoading}
        error={questionsError || error}
        responses={responses}
        saveQuestionResponse={saveQuestionResponse}
        startTime={new Date()}
        setAssessment={setAssessment}
        isPractice={isPractice}
        assessment={{ ...assessment, isPractice }}
        fullscreenInitiated={fullscreenPermissionGranted}
        autoStart={isPractice}
      />
    )
  }

  if (showAssessment && (!questionsLoaded || questionsLoading)) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          flexDirection: 'column',
          background: '#f8fafc',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1000
        }}
      >
        <LoadingSpinner size={48} />
        <h2 style={{
          marginTop: '24px',
          fontSize: '20px',
          fontWeight: '600',
          color: '#1e293b',
          fontFamily: 'inherit'
        }}>
          Preparing your role-based assessment...
        </h2>
        <p style={{
          marginTop: '8px',
          fontSize: '14px',
          color: '#64748b',
          fontFamily: 'inherit'
        }}>
          Please wait while we load your questions securely
        </p>
      </div>
    )
  }

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Custom Fullscreen Permission Modal */}
        {showFullscreenModal && <FullscreenPermissionModal />}

        {isTimerActive ? (
          <Timer timerPhase={timerPhase} countdown={countdown} />
        ) : (
          <>
            {/* Back button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px',
                cursor: 'pointer',
              }}
              onClick={handleBackAction}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: '8px' }}
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="#4F46E5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#4F46E5',
                }}
              >
                Back to Assessments
              </span>
            </div>

            <Modal
              title="System & Biometric Check"
              open={showConfig}
              onCancel={handleCancelConfig}
              footer={null}
              width={700}
              centered
              maskClosable={false}
              style={{ top: 20 }}
            >
              {!allChecksComplete ? (
                <ConfigCheck
                  onAllChecksComplete={() => {
                    setAllChecksComplete(true)
                    if (verificationEnabled) {
                      setBiometricRequested(true)
                    }
                  }}
                />
              ) : verificationEnabled && biometricRequested ? (
                <BiometricCheck
                  studentId={studentId}
                  onStartAssessment={handleStart}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#10003;</div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                    System Check Complete
                  </h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
                    Your device is ready. Click below to start the assessment.
                  </p>
                  <button
                    onClick={handleStart}
                    style={{
                      background: '#4F46E5',
                      color: '#ffffff',
                      border: 'none',
                      padding: '12px 32px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    Start Assessment
                  </button>
                </div>
              )}
            </Modal>

            <InstructionCard>
              <InstructionHeader>
                <div>
                  <InstructionTitle>Role-Based Assessment</InstructionTitle>
                  <div
                    style={{
                      fontSize: '30px',
                      fontWeight: '700',
                      color: '#000000',
                    }}
                  >
                    {assessment?.roleName}
                    {assessment?.seniority && ` (${assessment.seniority})`}
                  </div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <InstructionDeadline
                    style={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '22px',
                      lineHeight: '132%',
                      letterSpacing: '0%',
                    }}
                  >
                    Ends on&nbsp;
                    <span
                      style={{
                        color: '#3249D7',
                        marginLeft: '2px',
                        fontWeight: 'bold',
                      }}
                    >
                      {assessment?.endTime
                        ? moment.utc(assessment.endTime).format('Do MMMM, ha [IST]')
                        : '—'}
                    </span>
                  </InstructionDeadline>
                  <InstructionDuration>{durationLabel}</InstructionDuration>
                </div>
              </InstructionHeader>
            </InstructionCard>

            <InstructionCard>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                }}
              >
                <div style={{ marginRight: '8px' }}>
                  <Bulb />
                </div>
                <span
                  style={{
                    color: '#4F46E5',
                    fontWeight: '600',
                    fontSize: '26px',
                  }}
                >
                  Important!
                </span>
                <span
                  style={{
                    marginLeft: '8px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '26px',
                    lineHeight: '132%',
                    letterSpacing: '0%',
                  }}
                >
                  Instructions
                </span>
              </div>

              <ImportantNotice>
                <NoticeText style={{ fontStyle: 'italic' }}>
                  This is a comprehensive role-based assessment designed to evaluate your skills and knowledge for the specific job role. Assessment has to be taken in one go, please do not exit once started.
                </NoticeText>
              </ImportantNotice>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Assessment Structure
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  <strong>Multiple Choice Questions:</strong> Test your theoretical knowledge and understanding of key concepts
                </InstructionListItem>
                <InstructionListItem>
                  <strong>Written Response Questions:</strong> Demonstrate your analytical thinking and problem-solving abilities
                </InstructionListItem>
                <InstructionListItem>
                  <strong>Video Response:</strong> Showcase your communication skills and personality
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Technical Setup
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Ensure a stable internet connection throughout the assessment.
                </InstructionListItem>
                <InstructionListItem>
                  Use a laptop/desktop equipped with a working webcam, microphone, and speakers.
                </InstructionListItem>
                <InstructionListItem>
                  For video questions, make sure your camera and microphone are working properly.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Environment & Behavior
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  Sit in a well-lit, quiet environment with minimal background noise.
                </InstructionListItem>
                <InstructionListItem>
                  You can navigate between questions but try to answer all questions for the best evaluation.
                </InstructionListItem>
                <InstructionListItem>
                  The assessment is timed. Keep track of your remaining time.
                </InstructionListItem>
                <InstructionListItem>
                  Do not switch tabs or use unauthorized resources.
                </InstructionListItem>
                <InstructionListItem>
                  Do not refresh the page or close the browser during the assessment.
                </InstructionListItem>
              </InstructionList>

              <InstructionSectionTitle
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '22px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                }}
              >
                Monitoring
              </InstructionSectionTitle>
              <InstructionList>
                <InstructionListItem>
                  The system will monitor your screen, webcam, microphone, and audio for proctoring purposes.
                </InstructionListItem>
                <InstructionListItem>
                  Once you submit the assessment, you cannot make any changes.
                </InstructionListItem>
              </InstructionList>

            <ProctoringGuidelines proctoringEnabled={assessment?.allowProctoring !== false} />

              <ContactInfo
                style={{
                  textAlign: 'left',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '132%',
                  letterSpacing: '0%',
                  fontStyle: 'italic',
                  marginBottom: '18',
                  marginLeft: '10px',
                }}
              >
                If you face any technical issues during the assessment, please contact our support team immediately.
              </ContactInfo>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '24px',
                }}
              >
                <TakeAssessmentButton
                  onClick={handleTakeAssessment}
                  style={{
                    width: '302px',
                    height: '59px',
                    borderRadius: '8px',
                    fontSize: '18px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    padding: '0 24px',
                    marginTop: '24px',
                  }}
                >
                  Take assessment
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      marginLeft: '8px',
                      verticalAlign: 'middle',
                      position: 'relative',
                      top: '1px',
                    }}
                  >
                    <path
                      d="M5 12H19M19 12L12 19M19 12L12 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </TakeAssessmentButton>
              </div>
            </InstructionCard>
          </>
        )}
      </ContentWrapper>
    </PageContainer>
  )
}

export default RoleBasedInstructions